import { PocFileUploaderItem } from 'commons/components/components-wrapper/poc-file-uploader-item.component';
import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { DELIMITERS } from '@root/src/modules/operators/pods/private-pods/lms-import/constants/constants';
import { Select, TooltipedTextComponent } from 'commons/components';
import { FileSection, FileUploaderComponent } from 'commons/components/file-uploader';
import { useTranslation } from 'core/i18n/i18n';
import { FileUploadedLayout, MappingFileUploadLayout } from './training-registration-mapping-file-upload.style';

interface TrainingRegistrationMappingFileUploadComponentProps {
    filename: string;
    csvHeaders: string[];
    fileTypes: string[];
    isValidFile: boolean;
    delimiter?: string;
    onChangeDelimiter: (delimiter: string) => void;
    onLoadFile: (ev: React.ChangeEvent<HTMLInputElement>) => void;
    onResetFile: VoidFunction;
    isUploadOptionDisabled: boolean;
    isUpdating: boolean;
}

export const TrainingRegistrationMappingFileUploadComponent: React.FC<
    TrainingRegistrationMappingFileUploadComponentProps
> = (props) => {
    const {
        filename,
        delimiter,
        csvHeaders,
        fileTypes,
        isValidFile,
        onChangeDelimiter,
        onLoadFile,
        onResetFile,
        isUploadOptionDisabled = true,
        isUpdating,
    } = props;

    const { t } = useTranslation();

    const fileStatus = csvHeaders.length ? 'positive' : 'default';

    return (
        <MappingFileUploadLayout>
            <Select
                name={'fileDelimiter'}
                label={t('settings.integrations.lms-api-integration.mapping.file-upload.field.delimitter.label')}
                placeholder={t(
                    'settings.integrations.lms-api-integration.mapping.file-upload.field.delimitter.placeholder'
                )}
                required
                inline
                value={delimiter}
                options={Object.values(DELIMITERS).map((separator) => ({
                    value: separator,
                    text: t(`commons.delimiter.${separator}`),
                }))}
                disabled={!!filename}
                onChange={(_field, value) => onChangeDelimiter(value![0] as string)}
            />
            <FileSection>
                {filename ? (
                    <FileUploadedLayout>
                        <PocTypography variant="body1">{`${t(
                            'settings.integrations.lms-api-integration.mapping.file-upload.uploaded.title'
                        )}:`}</PocTypography>
                        <PocFileUploaderItem
                            value={100}
                            status={isValidFile ? fileStatus : 'alarm'}
                            progressBarProps={{ mode: 'determinate' }}
                            onDismiss={onResetFile}
                            dismissIconProps={{ id: 'dismissFile' }}
                        >
                            <div slot="prefix">
                                <TooltipedTextComponent
                                    i18nKey="<tooltip>{{filename}}</tooltip>"
                                    tooltipText={filename}
                                    i18nValues={{
                                        filename,
                                    }}
                                />
                            </div>
                            <div slot="suffix">
                                {t(
                                    `settings.integrations.lms-api-integration.mapping.file-upload.status.${
                                        isValidFile ? 'uploaded' : 'error'
                                    }`
                                )}
                            </div>
                        </PocFileUploaderItem>
                        <PocTooltip anchor="dismissFile">
                            {t('settings.integrations.lms-api-integration.mapping.file-upload.dismiss')}
                        </PocTooltip>
                        <PocTypography variant="body1">
                            {t(
                                `settings.integrations.lms-api-integration.mapping.file-upload.uploaded.${
                                    isValidFile ? 'info' : 'error'
                                }`
                            )}
                        </PocTypography>
                    </FileUploadedLayout>
                ) : (
                    <FileUploaderComponent
                        title={t('settings.integrations.lms-api-integration.mapping.file-upload.title') ?? undefined}
                        subtitle={
                            isUpdating
                                ? t('settings.integrations.lms-api-integration.mapping.file-upload.subtitle')
                                : undefined
                        }
                        buttonText={t('settings.integrations.lms-api-integration.mapping.file-upload.button')}
                        fileTypes={fileTypes}
                        onLoadFile={onLoadFile}
                        isUploadDisabled={isUploadOptionDisabled}
                    />
                )}
            </FileSection>
        </MappingFileUploadLayout>
    );
};
